import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LanguageProvider, useLanguage, detectLanguage } from "./contexts/LanguageContext";
import { LANGUAGES_CODES, ROUTE_SLUGS, SEO_PAGE_KEYS, routePath, SITE_URL } from "./i18n/routes";
import { tr } from "./utils/i18n";

// Components
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import ServicesSection from "./components/ServicesSection";
import BusinessModelSection from "./components/BusinessModelSection";
import StatsSection from "./components/StatsSection";
import BenefitsSection from "./components/BenefitsSection";
import TestimonialsSection from "./components/TestimonialsSection";
import IntegrationsSection from "./components/IntegrationsSection";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import AdminDashboard from "./components/AdminDashboard";
import LegalNotice from "./components/LegalNotice";
import SEOHead from "./components/SEOHead";
import SeoLandingPage from "./components/SeoLandingPage";

// Service Pages
import MSNService from "./components/services/MSNService";
import CouponService from "./components/services/CouponService";
import CommerceService from "./components/services/CommerceService";
import BNPLService from "./components/services/BNPLService";
import AutocompleteService from "./components/services/AutocompleteService";
import DeepLinkService from "./components/services/DeepLinkService";

// One-Pager
import OnePager from "./components/OnePager";
import PublishersPage from "./components/PublishersPage";

const Home = () => {
  const location = useLocation();
  const { currentLanguage } = useLanguage();
  const l = (text) => tr(currentLanguage, text);

  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        document.getElementById(location.state.scrollTo)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      window.history.replaceState({}, document.title);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "OptimHub",
      "url": SITE_URL,
      "logo": "https://customer-assets.emergentagent.com/job_traffic-partner/artifacts/hrx2swuq_logo2%20%281%29.png",
      "description": "DSP et traffic broker spécialisé en Content Commerce et Deep Link technology. OptimHub achète le trafic au CPC et les marchands paient au CPA.",
      "sameAs": ["https://www.linkedin.com/company/optimhub"],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Sales",
        "email": "contact@optimhub.com",
        "availableLanguage": ["French", "English", "German", "Italian", "Spanish", "Chinese"]
      },
      "areaServed": ["FR", "GB", "DE", "US", "IT", "ES", "CN"],
      "knowsAbout": [
        "Affiliate marketing",
        "Affiliate networks",
        "Content Commerce",
        "Deep Link Technology",
        "Programmatic advertising",
        "CPA monetization",
        "BNPL advertising",
        "MSN Network"
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "OptimHub",
      "url": SITE_URL,
      "inLanguage": LANGUAGES_CODES
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={l("OptimHub — Réseau et plateforme d'affiliation, une seule API")}
        description={l("Accédez à 28 plateformes et réseaux d'affiliation via une seule API OptimHub. Monétisation au CPC et au CPA, deep link technology et transparence des placements.")}
        keywords={l("réseau d'affiliation, plateforme d'affiliation, affiliation CPA, monétisation audience, deep link, content commerce, DSP, CPC vers CPA")}
        routeKey="home"
        lang={currentLanguage}
        schemas={schemas}
      />
      <Header />
      <main>
        <HeroSection />
        <ServicesSection />
        <BusinessModelSection />
        <StatsSection />
        <BenefitsSection />
        <TestimonialsSection />
        <IntegrationsSection />
        <ContactSection />
      </main>
      <Footer />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#374151',
            border: '1px solid #e5e7eb',
          },
        }}
      />
    </div>
  );
};

const PAGE_ELEMENTS = {
  home: <Home />,
  publishers: <PublishersPage />,
  onePager: <OnePager />,
  terms: <TermsOfService />,
  privacy: <PrivacyPolicy />,
  legalNotice: <LegalNotice />,
  serviceMsn: <MSNService />,
  serviceCoupon: <CouponService />,
  serviceCommerce: <CommerceService />,
  serviceBnpl: <BNPLService />,
  serviceAutocomplete: <AutocompleteService />,
  serviceDeepLink: <DeepLinkService />,
  ...Object.fromEntries(SEO_PAGE_KEYS.map((key) => [key, <SeoLandingPage pageKey={key} />]))
};

// The URL is the source of truth for the active language.
const LanguageRoute = ({ lang, children }) => {
  const { currentLanguage, switchLanguage } = useLanguage();

  useEffect(() => {
    if (currentLanguage !== lang) switchLanguage(lang);
  }, [lang, currentLanguage, switchLanguage]);

  return children;
};

const LegacyRedirect = ({ pageKey, hash = '' }) => (
  <Navigate to={`${routePath(pageKey, detectLanguage())}${hash}`} replace />
);

const localizedRoutes = LANGUAGES_CODES.flatMap((lang) =>
  Object.keys(ROUTE_SLUGS).map((key) => (
    <Route
      key={`${lang}-${key}`}
      path={routePath(key, lang)}
      element={<LanguageRoute lang={lang}>{PAGE_ELEMENTS[key]}</LanguageRoute>}
    />
  ))
);

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LegacyRedirect pageKey="home" />} />
            {localizedRoutes}

            {/* Legacy URLs */}
            <Route path="/publishers" element={<LegacyRedirect pageKey="publishers" />} />
            <Route path="/editeurs" element={<LegacyRedirect pageKey="publishers" />} />
            <Route path="/one-pager" element={<LegacyRedirect pageKey="onePager" />} />
            <Route path="/cgu" element={<LegacyRedirect pageKey="terms" />} />
            <Route path="/politique-confidentialite" element={<LegacyRedirect pageKey="privacy" />} />
            <Route path="/services/msn-microsoft-network" element={<LegacyRedirect pageKey="serviceMsn" />} />
            <Route path="/services/coupon-deals" element={<LegacyRedirect pageKey="serviceCoupon" />} />
            <Route path="/services/commerce-content" element={<LegacyRedirect pageKey="serviceCommerce" />} />
            <Route path="/services/bnpl" element={<LegacyRedirect pageKey="serviceBnpl" />} />
            <Route path="/services/browser-autocomplete" element={<LegacyRedirect pageKey="serviceAutocomplete" />} />
            <Route path="/services/deep-link-technology" element={<LegacyRedirect pageKey="serviceDeepLink" />} />
            <Route path="/OPTH-DEEPLINK.pdf" element={<LegacyRedirect pageKey="home" />} />

            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="*" element={<LegacyRedirect pageKey="home" />} />
          </Routes>
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
